/**
 * What a fall in a Crucible leads to for a Veil-Touched (see docs/gameplay.md,
 * "Crucible Outcome Rules", #94 and #255). Falling itself costs nothing; only
 * re-manifesting does. Values are in essence exchange value (see
 * `ESSENCE_EXCHANGE_RATE`) and are first-pass numbers to tune.
 */

import { resolveVeilSaturationEssenceScale, type VeilSaturationId } from './types/veil-saturation';

/**
 * Whether a body is down: its essence is spent (fallen for a player, dead
 * for an enemy). The one place the rule lives (#327).
 */
export function isDown(body: { readonly essence: number }): boolean {
    return body.essence <= 0;
}

/**
 * Price of the first re-manifest in a Faint run; it doubles with each death
 * after. Fuller bands scale it as their essence pools do (#269), so it stays
 * a like share of what a run holds: cheap for a new character, never trivial
 * in Overflowing.
 */
export const REMANIFEST_BASE_PRICE_VALUE = 20;

/** How long a fallen player has to choose before the Crucible casts them out. */
export const FALLEN_DECISION_MS = 10_000;

/**
 * The re-manifest price after a player's `deathsThisRun`th death (1-based)
 * in a run of `veilSaturation`: Faint 20, 40, 80, ...; Overflowing 140, 280, 560, ...
 */
export function resolveRemanifestPrice(deathsThisRun: number, veilSaturation: VeilSaturationId): number {
    const firstPrice = Math.round(REMANIFEST_BASE_PRICE_VALUE * resolveVeilSaturationEssenceScale(veilSaturation));
    return firstPrice * 2 ** Math.max(0, deathsThisRun - 1);
}
